
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Services() {
  const [activeService, setActiveService] = useState('consultation');

  const services = [
    {
      id: 'consultation',
      icon: 'ri-user-voice-line',
      title: 'Equipment Consultation',
      subtitle: 'Expert Guidance for Your Laundry Needs',
      description: 'Our experienced team provides comprehensive consultation services to help you select the right equipment for your specific requirements. We analyze your workflow, capacity needs, and budget to recommend optimal solutions.',
      features: [
        'Free on-site assessment and consultation',
        'Customized equipment recommendations',
        'Capacity planning and workflow optimization',
        'Budget analysis and ROI calculations',
        'Energy efficiency recommendations',
        'Space utilization planning'
      ],
      image: 'Professional laundry consultant analyzing equipment needs in modern commercial facility, discussing with client, technical drawings and tablets visible, bright professional lighting'
    },
    {
      id: 'planning',
      icon: 'ri-draft-line',
      title: 'Project Planning',
      subtitle: 'Complete End-to-End Project Management',
      description: 'From initial concept to final implementation, we handle every aspect of your laundry facility project. Our project management ensures timely delivery, quality installation, and seamless integration.',
      features: [
        'Detailed project timeline and milestones',
        'Site preparation and utility requirements',
        'Equipment layout and workflow design',
        'Permit assistance and compliance guidance',
        'Vendor coordination and management',
        'Quality assurance and progress monitoring'
      ],
      image: 'Modern laundry facility construction planning, architects and engineers reviewing blueprints, 3D models on screens, construction site background, professional project management atmosphere'
    },
    {
      id: 'installation',
      icon: 'ri-tools-line',
      title: 'Installation & Commissioning',
      subtitle: 'Professional Setup & Testing',
      description: 'Our certified technicians ensure proper installation and commissioning of all equipment. We handle everything from mechanical connections to final testing and staff training.',
      features: [
        'Certified technician installation',
        'Utility connections and testing',
        'Equipment calibration and optimization',
        'Safety system verification',
        'Performance testing and validation',
        'Documentation and warranty activation'
      ],
      image: 'Professional technicians installing commercial laundry equipment, wearing safety gear, modern industrial setting with large washing machines and dryers, technical precision work'
    },
    {
      id: 'maintenance',
      icon: 'ri-tools-fill',
      title: 'Maintenance & Support',
      subtitle: '24/7 Technical Support & Service',
      description: 'Keep your equipment running at peak performance with our comprehensive maintenance programs. We offer preventive maintenance, emergency repairs, and ongoing technical support.',
      features: [
        '24/7 emergency service hotline',
        'Preventive maintenance programs',
        'Original spare parts guarantee',
        'Remote diagnostics and monitoring',
        'Performance optimization services',
        'Extended warranty options'
      ],
      image: 'Maintenance technician performing routine service on commercial washing machine, diagnostic tools and spare parts visible, clean professional maintenance bay, quality service focus'
    },
    {
      id: 'training',
      icon: 'ri-graduation-cap-line',
      title: 'Training & Education',
      subtitle: 'Comprehensive Staff Training Programs',
      description: 'Maximize your investment with proper training. We provide comprehensive training programs for your staff to ensure optimal operation, safety, and equipment longevity.',
      features: [
        'Operation and safety training',
        'Maintenance best practices',
        'Troubleshooting and diagnostics',
        'Efficiency optimization techniques',
        'Safety protocols and procedures',
        'Certification programs available'
      ],
      image: 'Training session in modern laundry facility, instructor demonstrating equipment operation to group of staff members, interactive learning environment, professional development focus'
    }
  ];

  const industries = [
    {
      icon: 'ri-hotel-line',
      title: 'Hospitality',
      description: 'Hotels, resorts, and restaurants requiring high-capacity laundry solutions.'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Healthcare',
      description: 'Hospitals and medical facilities with specialized hygiene requirements.'
    },
    {
      icon: 'ri-school-line',
      title: 'Educational',
      description: 'Schools, universities, and dormitories managing large volumes of laundry.'
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial',
      description: 'Laundromats and commercial laundry service providers.'
    },
    {
      icon: 'ri-building-line',
      title: 'Industrial',
      description: 'Manufacturing and industrial facilities with specialized washing needs.'
    },
    {
      icon: 'ri-government-line',
      title: 'Government',
      description: 'Public institutions and government facilities requiring reliable solutions.'
    }
  ];

  const currentService = services.find(service => service.id === activeService);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section - Aligned with Partners Page Design */}
      <section className="pt-32 pb-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-5"
            style={{
              backgroundImage: `url('/images/backgrounds/services-hero-bg.jpg')`
            }}
          ></div>
        </div>

        {/* Clean Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-32 right-20 w-12 h-12 bg-teal-100 rounded-full opacity-25 animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-10 h-10 bg-gray-100 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-blue-100 rounded-full opacity-30 animate-bounce" style={{ animationDelay: '2s' }}></div>

        {/* Simple Icon Elements */}
        <div className="absolute top-40 left-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float">
          <i className="ri-tools-line text-blue-500 text-lg"></i>
        </div>
        <div className="absolute bottom-32 right-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float" style={{ animationDelay: '1.5s' }}>
          <i className="ri-customer-service-2-line text-teal-500 text-lg"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm font-semibold shadow-sm">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-customer-service-2-line text-sm text-white"></i>
                </div>
                Comprehensive Service Solutions
                <span className="w-2 h-2 bg-blue-500 rounded-full ml-3"></span>
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
              Complete Laundry{' '}
              <span className="text-blue-600">
                Solutions
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              From consultation to ongoing support, we provide comprehensive services to ensure your laundry operations run smoothly and efficiently.
            </p>
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(5deg);
            }
          }
          .animate-float {
            animation: animate-float 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Action Buttons */}
      <section className="py-8 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Buttons removed as requested */}
        </div>
      </section>

      {/* Services Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white shadow-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {services.map((service) => (
              <div key={service.id} className="group/btn relative">
                <div className={`absolute inset-0 ${service.id === activeService ? 'bg-blue-700' : 'bg-gray-300'} rounded-full transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300`}></div>
                <button
                  onClick={() => setActiveService(service.id)}
                  className={`relative ${
                    activeService === service.id
                      ? 'bg-blue-600 text-white border-white/20'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  } font-medium px-6 py-3 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl whitespace-nowrap border-2`}
                >
                  <i className={`${service.icon} mr-2`}></i>
                  {service.title}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Service Detail */}
      {currentService && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                    <i className={`${currentService.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{currentService.title}</h2>
                    <p className="text-blue-600 font-medium">{currentService.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-gray-600 text-lg mb-8">{currentService.description}</p>
                
                <div className="space-y-4 mb-8">
                  {currentService.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <i className="ri-check-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="group/btn relative">
                    <div className="absolute inset-0 bg-blue-700 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                    <button className="relative bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap border-2 border-white/20">
                      <span className="flex items-center">
                        <i className="ri-phone-line mr-2"></i>
                        Get This Service
                      </span>
                    </button>
                  </div>
                  <div className="group/btn relative">
                    <div className="absolute inset-0 bg-gray-300 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                    <button className="relative bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-800 font-bold px-6 py-3 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap">
                      <span className="flex items-center">
                        <i className="ri-information-line mr-2"></i>
                        Learn More
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/images/services/service-illustration.jpg"
                    alt={currentService.title}
                    className="w-full h-[400px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/10"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Industries Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50 relative overflow-hidden">
        {/* Beautiful Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{
              backgroundImage: `url('/images/backgrounds/industries-services-bg.jpg')`,
            }}
          />

          {/* Floating Elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
          <div className="absolute top-1/4 right-1/3 w-36 h-36 bg-cyan-200/30 rounded-full blur-2xl animate-pulse delay-1500"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Specialized solutions tailored for different industry requirements and standards.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {industries.map((industry, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-4 border border-white/20 relative overflow-hidden h-full transform hover:scale-105">
                  {/* Card Background Elements */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-blue-600/10 rounded-full blur-xl"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full"></div>

                  {/* Floating Particle Effects */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-blue-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div
                    className="absolute bottom-2 left-2 w-1 h-1 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700"
                    style={{ animationDelay: '0.5s' }}
                  ></div>

                  <div className="relative z-10">
                    {/* 3D Icon Container */}
                    <div className="relative mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative">
                        {/* 3D Shadow Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
                        <i className={`${industry.icon} text-white text-3xl group-hover:scale-110 transition-transform duration-300`}></i>

                        {/* Icon Glow Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/50 to-blue-400/50 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                      </div>

                      {/* Floating Ring */}
                      <div className="absolute inset-0 w-20 h-20 border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                    </div>

                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-700 transition-colors duration-300">
                      {industry.title}
                    </h3>

                    {/* Animated Underline */}
                    <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mb-6 group-hover:w-20 transition-all duration-500"></div>

                    <p className="text-gray-600 leading-relaxed text-lg group-hover:text-gray-700 transition-colors duration-300">
                      {industry.description}
                    </p>

                    {/* Interactive Progress Bar */}
                    <div className="mt-8 h-2 bg-gray-200/50 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-teal-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-6 right-6 w-10 h-10 bg-gradient-to-br from-blue-500/20 to-teal-500/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Reflection Effect */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-indigo-200/25 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-slate-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
          <div className="absolute top-1/4 right-1/4 w-28 h-28 bg-cyan-200/20 rounded-full blur-2xl animate-pulse delay-1500"></div>
          
          {/* Floating Geometric Shapes */}
          <div className="absolute top-32 right-1/3 w-8 h-8 bg-blue-400/20 rotate-45 animate-bounce delay-200"></div>
          <div className="absolute bottom-32 left-1/4 w-6 h-6 bg-indigo-400/25 rounded-full animate-bounce delay-700"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/60 backdrop-blur-xl border border-white/30 text-blue-800 text-sm font-semibold shadow-xl mb-6">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                <i className="ri-flow-chart text-blue-600 text-sm"></i>
              </div>
              Our Proven Methodology
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-3 animate-pulse"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Our Service Process
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              A proven methodology that ensures successful project delivery and customer satisfaction through systematic excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Initial Consultation',
                description: 'We assess your needs and provide expert recommendations.',
                icon: 'ri-discuss-line',
                color: 'blue',
                delay: '0s'
              },
              {
                step: '02',
                title: 'Planning & Design',
                description: 'Detailed project planning with timeline and specifications.',
                icon: 'ri-draft-line',
                color: 'indigo',
                delay: '0.3s'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'Professional installation and commissioning by certified technicians.',
                icon: 'ri-tools-line',
                color: 'purple',
                delay: '0.6s'
              },
              {
                step: '04',
                title: 'Ongoing Support',
                description: 'Continuous support, maintenance, and optimization services.',
                icon: 'ri-customer-service-2-line',
                color: 'teal',
                delay: '0.9s'
              }
            ].map((process, index) => (
              <div 
                key={index} 
                className="group process-card relative"
                style={{ 
                  animation: `slideInUp 0.8s ease-out ${process.delay} both`,
                  opacity: 0 
                }}
              >
                {/* Connection Line */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-12 left-20 w-full h-0.5 bg-gradient-to-r from-white/60 via-blue-300/40 to-white/60 z-0">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-1000"
                      style={{ animationDelay: `${1.2 + index * 0.3}s` }}
                    ></div>
                  </div>
                )}

                {/* Main Card Container */}
                <div className="relative bg-white/40 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-2 border border-white/30 overflow-hidden h-full">
                  {/* Glass Morphism Layers */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
                  <div className="absolute inset-0 bg-gradient-to-tl from-blue-500/5 via-transparent to-indigo-500/5 rounded-3xl"></div>
                  
                  {/* Floating Particles Container */}
                  <div className="absolute inset-0 overflow-hidden rounded-3xl">
                    <div className="particle absolute top-4 right-4 w-1 h-1 bg-blue-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                    <div className="particle absolute top-8 left-6 w-1.5 h-1.5 bg-indigo-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>
                    <div className="particle absolute bottom-6 right-8 w-1 h-1 bg-purple-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-600" style={{ animationDelay: '0.6s' }}></div>
                    <div className="particle absolute bottom-4 left-4 w-0.5 h-0.5 bg-teal-400/70 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-800" style={{ animationDelay: '0.9s' }}></div>
                  </div>

                  <div className="relative z-10 text-center">
                    {/* Step Number with Glass Effect */}
                    <div className="relative mb-6">
                      {/* Step Number Background Layers */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-white/20 rounded-2xl backdrop-blur-sm transform rotate-6 group-hover:rotate-12 transition-transform duration-500"></div>
                      <div className="absolute inset-0 bg-gradient-to-tl from-blue-500/20 to-indigo-500/10 rounded-2xl backdrop-blur-sm transform -rotate-3 group-hover:-rotate-6 transition-transform duration-500"></div>
                      
                      <div className={`relative w-16 h-16 bg-gradient-to-br ${
                        process.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        process.color === 'indigo' ? 'from-indigo-500 to-indigo-600' :
                        process.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        'from-teal-500 to-teal-600'
                      } rounded-2xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 transition-all duration-500 border border-white/30`}>
                        <i className={`${process.icon} text-white text-2xl group-hover:scale-110 transition-transform duration-300`}></i>
                        
                        {/* Icon Glow */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${
                          process.color === 'blue' ? 'from-blue-400/50 to-blue-400/30' :
                          process.color === 'indigo' ? 'from-indigo-400/50 to-indigo-400/30' :
                          process.color === 'purple' ? 'from-purple-400/50 to-purple-400/30' :
                          'from-teal-400/50 to-teal-400/30'
                        } rounded-2xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500`}></div>
                      </div>

                      {/* Step Number Badge */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-white/90 backdrop-blur-xl rounded-full flex items-center justify-center shadow-xl border border-white/50 group-hover:scale-110 transition-transform duration-300">
                        <span className={`font-bold text-sm ${
                          process.color === 'blue' ? 'text-blue-600' :
                          process.color === 'indigo' ? 'text-indigo-600' :
                          process.color === 'purple' ? 'text-purple-600' :
                          'text-teal-600'
                        }`}>
                          {process.step}
                        </span>
                      </div>
                    </div>

                    {/* Content Container with Glass Effect */}
                    <div className="relative bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors duration-300">
                        {process.title}
                      </h3>
                      
                      {/* Animated Underline */}
                      <div className={`w-8 h-1 bg-gradient-to-r ${
                        process.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        process.color === 'indigo' ? 'from-indigo-500 to-indigo-600' :
                        process.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        'from-teal-500 to-teal-600'
                      } rounded-full mx-auto mb-4 group-hover:w-16 transition-all duration-500 shadow-lg`}></div>
                      
                      <p className="text-gray-700 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                        {process.description}
                      </p>

                      {/* Progress Indicator */}
                      <div className="mt-6 h-1 bg-white/30 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${
                          process.color === 'blue' ? 'from-blue-500 to-blue-600' :
                          process.color === 'indigo' ? 'from-indigo-500 to-indigo-600' :
                          process.color === 'purple' ? 'from-purple-500 to-purple-600' :
                          'from-teal-500 to-teal-600'
                        } rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg`}></div>
                      </div>
                    </div>

                    {/* Hover Indicator */}
                    <div className="absolute bottom-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 border border-white/40">
                      <i className="ri-arrow-right-line text-gray-700 text-sm group-hover:translate-x-0.5 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Subtle Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom CSS for animations */}
        <style jsx>{`
          @keyframes slideInUp {
            0% {
              opacity: 0;
              transform: translateY(60px) scale(0.9);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .process-card {
            animation-fill-mode: both;
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(3deg);
            }
          }

          .particle {
            animation-duration: 2s;
            animation-iteration-count: infinite;
          }

          /* Intersection Observer Animation */
          .process-card.animate-in {
            animation: slideInUp 0.8s ease-out both;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-700/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Laundry Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let our experts help you design and implement the perfect laundry solution for your business needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="group/btn relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
              <Link to="/contact">
                <button className="relative bg-white text-blue-600 hover:bg-gray-50 font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                  <span className="flex items-center">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-phone-line text-sm text-blue-600"></i>
                    </div>
                    Schedule Free Consultation
                  </span>
                </button>
              </Link>
            </div>
            <div className="group/btn relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
              <Link to="/contact">
                <button className="relative bg-white/10 border-2 border-white/30 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap">
                  <span className="flex items-center">
                    <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-calculator-line text-sm"></i>
                    </div>
                    Get Custom Quote
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
